/**
 * Life4Earth V2 — Role Bundles API adapter (DEV-1213 backend / DEV-1214 UI).
 *
 * Wraps the sysadmin authoring + community picker endpoints exposed by
 * RoleBundlesController and CommunityRoleBundleAssignmentController.
 *
 * Backend vocabulary mapping (migration 220, 2026-06-04):
 *   * catalog_presets table       → role_bundles
 *   * community_preset_assignment → community_role_bundle_assignments
 *   * role_definitions.preset_guid → role_bundle_guid
 *   * controllers + endpoints renamed; this adapter exposes the new shape.
 *
 * Endpoints:
 *   Sysadmin authoring:
 *     GET    /api/admin/role-bundles                         — list all (incl inactive)
 *     GET    /api/admin/role-bundles/{guid}                  — single detail
 *     POST   /api/admin/role-bundles                         — create canonical
 *     PUT    /api/admin/role-bundles/{guid}                  — update mutable fields
 *     DELETE /api/admin/role-bundles/{guid}                  — soft-retire
 *     GET    /api/admin/role-bundles/{guid}/roles            — roles in bundle
 *     POST   /api/admin/role-definitions/{guid}/attach-bundle — set role_bundle_guid
 *
 *   Community-facing:
 *     GET    /api/role-bundles/available?communityGuid=      — picker source
 *     GET    /api/community-role-bundle-assignment/{guid}    — current assignment
 *     POST   /api/community-role-bundle-assignment/{guid}/apply
 *     DELETE /api/community-role-bundle-assignment/{guid}
 */

import { apiFetch } from "./client";
import { invalidateByPrefix } from "./queryCache";

/**
 * DEV-1215 follow-up (2026-06-05). Invalidate the effective-role-
 * definitions cache for a community after any bundle mutation. This
 * pokes useApiEffectiveRoleDefinitions (which keys on
 * `roleDef:effective:{communityGuid}:{pillarType}`) so UnifiedTeamTab's
 * Catalog Reference area picks up newly-available bundled roles
 * without a hard reload.
 *
 * Skip bug 2026-06-05: applying a bundle didn't surface the bundled
 * roles in Team & Roles until reload. SignalR's SecurityUpdated fired
 * but the UnifiedTeamTab listener only refreshed slots/pool, not the
 * catalog hook. Invalidating at the API boundary covers every consumer.
 */
function invalidateEffectiveCatalog(communityGuid: string): void {
  // The hook's key shape is `roleDef:effective:{guid}:{pillarType}`.
  // Invalidate by community-guid prefix so any pillar filter under that
  // entity flushes (the hook re-fetches based on its own props).
  invalidateByPrefix(`roleDef:effective:${communityGuid.toLowerCase()}:`);
}

// ════════════════════════════════════════════════════════════════
// Response types — mirror server-side RoleBundleDtos.cs
// ════════════════════════════════════════════════════════════════

/**
 * Full read shape for a single Role Bundle. Used by sysadmin authoring
 * views and the community-facing detail endpoint.
 */
export interface RoleBundleResponse {
  roleBundleGuid: string;
  bundleKey: string;
  label: string;
  description: string | null;
  applicableEntityTypes: string[];
  audienceEntityGuids: string[];
  applicableProjectCategories: string[];
  owningEntityGuid: string | null;
  isSystemLocked: boolean;
  isActive: boolean;
  createdByUserGuid: string | null;
  createdAt: string;
  updatedAt: string;
  /** Active role_definitions whose role_bundle_guid points at this bundle. */
  roleCount: number;
}

/**
 * Lighter shape used by the community-facing "bundles I can apply" picker.
 */
export interface RoleBundleSummaryResponse {
  roleBundleGuid: string;
  bundleKey: string;
  label: string;
  description: string | null;
  roleCount: number;
}

/** One role in a bundle's role list. */
export interface RoleBundleRoleSummaryResponse {
  definitionGuid: string;
  label: string;
  roleCategory: string;
  pillarType: string;
  isActive: boolean;
  hasRhythm: boolean;
  /** DEV-1214 iter 3.5 — global sort_order on role_definitions. Drives
   *  the bundle role-list order and the drag-reorder UI. */
  sortOrder: number;
}

/** Community-side: which bundle (if any) is currently applied. */
export interface CommunityRoleBundleAssignmentResponse {
  communityEntityGuid: string;
  roleBundleGuid: string;
  bundleKey: string;
  bundleLabel: string;
  appliedAt: string;
  appliedByUserGuid: string | null;
}

// ════════════════════════════════════════════════════════════════
// Write request shapes
// ════════════════════════════════════════════════════════════════

export interface CreateRoleBundleRequest {
  /** Must start with `bundle_` (server-enforced CHECK constraint). */
  bundleKey: string;
  label: string;
  description?: string | null;
  applicableEntityTypes?: string[];
  audienceEntityGuids?: string[];
  applicableProjectCategories?: string[];
}

export interface UpdateRoleBundleRequest {
  label?: string;
  description?: string | null;
  applicableEntityTypes?: string[];
  audienceEntityGuids?: string[];
  applicableProjectCategories?: string[];
  isActive?: boolean;
}

export interface AttachRoleBundleRequest {
  /** Null = detach (clear role_bundle_guid). */
  roleBundleGuid: string | null;
}

export interface ApplyRoleBundleRequest {
  roleBundleGuid: string;
}

// ════════════════════════════════════════════════════════════════
// Sysadmin authoring endpoints
// ════════════════════════════════════════════════════════════════

/** List all Role Bundles (including inactive) for the sysadmin authoring page. */
export async function fetchAllRoleBundlesAdmin(): Promise<RoleBundleResponse[]> {
  return apiFetch<RoleBundleResponse[]>("/api/admin/role-bundles");
}

/** Single Role Bundle detail for the sysadmin authoring page. */
export async function fetchRoleBundleAdmin(
  roleBundleGuid: string,
): Promise<RoleBundleResponse> {
  return apiFetch<RoleBundleResponse>(`/api/admin/role-bundles/${roleBundleGuid}`);
}

/** Create a canonical Role Bundle (sysadmin only). */
export async function createRoleBundle(
  req: CreateRoleBundleRequest,
): Promise<RoleBundleResponse> {
  return apiFetch<RoleBundleResponse>("/api/admin/role-bundles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/** Update mutable fields on a Role Bundle. Null fields = no-op. */
export async function updateRoleBundle(
  roleBundleGuid: string,
  req: UpdateRoleBundleRequest,
): Promise<RoleBundleResponse> {
  return apiFetch<RoleBundleResponse>(`/api/admin/role-bundles/${roleBundleGuid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/** Soft-retire a Role Bundle (is_active = false). Idempotent. */
export async function retireRoleBundle(roleBundleGuid: string): Promise<void> {
  await apiFetch<void>(`/api/admin/role-bundles/${roleBundleGuid}`, {
    method: "DELETE",
  });
}

/** List active roles belonging to a Role Bundle. Sysadmin only. */
export async function fetchRoleBundleRoles(
  roleBundleGuid: string,
): Promise<RoleBundleRoleSummaryResponse[]> {
  return apiFetch<RoleBundleRoleSummaryResponse[]>(
    `/api/admin/role-bundles/${roleBundleGuid}/roles`,
  );
}

/**
 * Attach (or detach with roleBundleGuid=null) a Role Bundle to a role
 * definition. Sysadmin only. When attached, the role surfaces in the
 * effective catalog only for communities that have applied the bundle.
 */
export async function attachRoleBundleToDefinition(
  definitionGuid: string,
  roleBundleGuid: string | null,
): Promise<void> {
  await apiFetch<void>(
    `/api/admin/role-definitions/${definitionGuid}/attach-bundle`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roleBundleGuid } satisfies AttachRoleBundleRequest),
    },
  );
}

/**
 * DEV-1214 iter 3.5. Set a single role_definition's sort_order. Called
 * by the bundle drag-to-reorder UI in a sequential burst after a drop.
 * Sysadmin only. See RoleDefinitionsController.SetSortOrder for the
 * global-vs-bundle-ordering trade-off captured in this endpoint.
 */
export async function setRoleDefinitionSortOrder(
  definitionGuid: string,
  sortOrder: number,
): Promise<void> {
  await apiFetch<void>(
    `/api/admin/role-definitions/${definitionGuid}/sort-order`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sortOrder }),
    },
  );
}

// ════════════════════════════════════════════════════════════════
// Community-facing endpoints
// ════════════════════════════════════════════════════════════════

/**
 * "Bundles I can apply" picker. Filtered by entity_type whitelist and
 * audience descent. Returns active canonical bundles only.
 */
export async function fetchAvailableRoleBundles(
  communityGuid: string,
): Promise<RoleBundleSummaryResponse[]> {
  return apiFetch<RoleBundleSummaryResponse[]>(
    `/api/role-bundles/available?communityGuid=${communityGuid}`,
  );
}

/**
 * Get the community's current Role Bundle assignment. Returns null
 * (204 → undefined unwrapped to null) when no bundle is applied.
 */
export async function fetchCommunityRoleBundleAssignment(
  communityGuid: string,
): Promise<CommunityRoleBundleAssignmentResponse | null> {
  try {
    return await apiFetch<CommunityRoleBundleAssignmentResponse>(
      `/api/community-role-bundle-assignment/${communityGuid}`,
    );
  } catch {
    // 204 No Content is the "no assignment" case; apiFetch surfaces it
    // as a throw on parse. Treat as null.
    return null;
  }
}

/** Apply a Role Bundle to a community. Silent upsert (replaces any current). */
export async function applyRoleBundleToCommunity(
  communityGuid: string,
  roleBundleGuid: string,
): Promise<CommunityRoleBundleAssignmentResponse> {
  const result = await apiFetch<CommunityRoleBundleAssignmentResponse>(
    `/api/community-role-bundle-assignment/${communityGuid}/apply`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roleBundleGuid } satisfies ApplyRoleBundleRequest),
    },
  );
  invalidateEffectiveCatalog(communityGuid);
  return result;
}

/** Un-apply the community's current Role Bundle. Idempotent. */
export async function unapplyCommunityRoleBundle(
  communityGuid: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/community-role-bundle-assignment/${communityGuid}`,
    { method: "DELETE" },
  );
  invalidateEffectiveCatalog(communityGuid);
}
